import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
  vnCartMessage,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// VINEY (T672 rule 3, session 3): the cart freshness gate, shared through two window numbers with
// product-form.js, cart-icon.js and vn-motion.js. Every request that reads or changes the cart is numbered
// when sent (__vnCartRead); its answer is applied only if no newer cart has been shown (__vnCartFresh),
// and applying it moves the line to its number. So no slow answer can paint an older cart over a newer one.
const vnCart = {
  next: () => {
    const w = /** @type {any} */ (window);
    return (w.__vnCartRead = (w.__vnCartRead || 0) + 1);
  },
  /** @param {number} n */
  stale: (n) => n < (/** @type {any} */ (window).__vnCartFresh || 0),
  /** @param {number} n */
  seen: (n) => {
    const w = /** @type {any} */ (window);
    w.__vnCartFresh = Math.max(w.__vnCartFresh || 0, n);
  },
  // Send order is not always the order the store applied things in, so an answer set aside as stale is
  // followed by one reconciliation (debounced, shared by every file through window): a fresh numbered read
  // of the cart and of every cart section, sent after the answer arrived, so the last thing shown is the
  // store's cart as it settled. Listeners: component-cart-items.js, cart-icon.js, vn-motion.js (the vine).
  reconcile: () => {
    const w = /** @type {any} */ (window);
    clearTimeout(w.__vnReconcileTimer);
    w.__vnReconcileTimer = setTimeout(() => {
      const e = /** @type {any} */ (new CustomEvent('vn:cart-reconcile'));
      e.vnGen = vnCart.next();
      document.dispatchEvent(e);
    }, 300);
  },
};

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('vn:cart-reconcile', this.#vnOnReconcile);
    window.addEventListener('pageshow', this.#vnOnPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('vn:cart-reconcile', this.#vnOnReconcile);
    window.removeEventListener('pageshow', this.#vnOnPageShow);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#vnRow(line) ?? this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) return row.remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      // VINEY: unless a redraw has since given the row back (it takes the class off, and that ends the animation too)
      onAnimationEnd(row, () => {
        if (row.classList.contains('removing')) row.remove();
      });
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    // VINEY: the line is named by its key. A number only says where a line was when this list was drawn, so after the
    // cart changed elsewhere (another tab, Back to a page kept in the browser's cache) it is refused or names another product.
    const vnKey = this.#vnRow(line)?.dataset.key;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(vnKey ? { id: vnKey } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    // VINEY: a line change is numbered when sent (the freshness gate above)
    const vnRead = vnCart.next();
    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        Object.defineProperty(parsedResponseText, '__vnRead', { value: vnRead });

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // VINEY: a refused removal has already taken its row off the screen, and a line the store no longer has
          // cannot be corrected by the shopper: both show the store's cart again instead of an error about a stale line
          if (config.action === 'clear' || /\b(id|line)\b.*\bparameter\b/i.test(String(parsedResponseText.errors))) {
            vnCart.reconcile();
            return;
          }
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // VINEY: an answer overtaken by a newer cart is not painted; a reconciliation read follows
        if (vnCart.stale(vnRead)) {
          vnCart.reconcile();
          return;
        }
        vnCart.seen(vnRead);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        // VINEY: no readable answer (a throttled request answers with a page, not JSON): what is shown may be wrong
        vnCart.reconcile();
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    // VINEY (S9, T674): a quantity-limit refusal names unit counts, so it is said without them
    // (utilities.js vnCartMessage); any other validation error keeps Shopify's own text
    cartItemError.textContent = String(vnCartMessage(parsedResponseText.errors));
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    // VINEY: every section an event refreshes (the drawer and /cart) shares one number, kept on the event,
    // so their answers, which show the same cart, cannot set each other aside
    const ev = /** @type {any} */ (event);
    if (event instanceof DiscountUpdateEvent) {
      this.#vnRefresh((ev.__vnGen ??= vnCart.next()));
      return;
    }
    if (event.target === this) return;

    // VINEY: an event carrying an older cart than the newest shown changes nothing here
    const vnRead = /** @type {any} */ (event.detail?.resource)?.__vnRead;
    if (vnRead !== undefined && vnCart.stale(vnRead)) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      this.#vnRefresh((ev.__vnGen ??= vnCart.next()));
    }
  };

  #vnOnReconcile = (/** @type {any} */ event) => this.#vnRefresh(event.vnGen);

  // VINEY: Back can restore a page from the browser's cache with the list drawn before the cart last changed
  #vnOnPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) vnCart.reconcile();
  };

  /**
   * The row a line number belongs to, found by the row's own trash button: the numbers on the page stay as drawn until
   * the next redraw, while the list of rows shrinks as soon as one is taken away.
   * @param {number} line
   * @returns {HTMLElement | null}
   */
  #vnRow(line) {
    const button = this.querySelector(`.cart-items__remove[on\\:click="/onLineItemRemove/${line}"]`);
    return /** @type {HTMLElement | null} */ (button?.closest('[data-key]') ?? null);
  }

  // VINEY: Horizon's fresh render of this section (renderSection with no cache), under the number of the
  // event that asked for it, morphed only if no newer cart has been shown by the time it answers. The number
  // goes into the address, so a newer refresh never shares an older one's request (section-renderer.js
  // shares one request per address).
  /** @param {number} n */
  #vnRefresh = async (n) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('vn', String(n));
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      if (vnCart.stale(n)) {
        vnCart.reconcile();
        return;
      }
      vnCart.seen(n);
      morphSection(this.sectionId, html);
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
